import { useState } from "react";
import { Upload, Typography, Flex, Space, Button, App, Progress } from "antd";
import { BTN_TXT, DESC_TXT } from "../../constants/constants";
import "./DocUploadCard.scss";
import { handleDeleteFile, handleUploadFile } from "../../helpers/helpers";

const { Text } = Typography;

const BusEntDocUploadCard = ({ doc, file, setFile, busEntId }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { message } = App.useApp();

  const beforeUpload = (file) => {
    const isLt20MB = file.size / 1024 / 1024 < 20;
    if (!isLt20MB) {
      message.error("File must be smaller than 20MB!");
      return false;
    }

    if (!doc.allowedTypes.includes(file.type)) {
      message.error(`Please upload a valid ${doc.type} file.`);
      return false;
    }

    return true;
  };

  const uploadFile = async (file) => {
    try {
      const cleanName = file.name.replace(/\s+/g, "_");
      const cleanFile = new File([file], cleanName, { type: file.type });
      const uploadPath = `busEnt/${busEntId}`;

      setUploading(true);
      setProgress(0);

      const res = await handleUploadFile(cleanFile, uploadPath, setProgress);

      setFile(res);
      setProgress(100);
      setUploading(false);
    } catch (err) {
      setUploading(false);
      setProgress(0);
      message.error(err?.message || "File upload failed");
    }
  };

  const deleteFile = async (fileUrl) => {
    try {
      await handleDeleteFile(fileUrl);
      setFile({ fileUrl: null, fileName: null });
      message.success("File deleted");
    } catch (err) {
      message.error(err?.message || "Failed to delete file");
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    maxCount: 1,
    accept: ".zip",
    beforeUpload: (file) => beforeUpload(file),
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        await uploadFile(file);
        onSuccess("ok");
      } catch (e) {
        onError(e);
      }
    },
  };

  return (
    <div className="doc-upload-card">
      <Flex align="center" justify="space-between" gap={20}>
        <Space direction="vertical" size="small">
          <Text className="file-title">{doc.title}</Text>
          {file.fileUrl ? (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                {file.fileName || file.fileUrl.split("/").pop()}
              </Text>
            </div>
          ) : (
            <Text className="file-desc">{`${DESC_TXT.UPLOAD_DOC_DESC} ${doc.type}`}</Text>
          )}
        </Space>

        <Flex gap={8}>
          {!file.fileUrl ? (
            <Upload {...uploadProps} accept={doc.allowedTypes.join(", ")}>
              <Button type="primary">{BTN_TXT.UPLOAD}</Button>
            </Upload>
          ) : (
            <Button danger onClick={() => deleteFile(file.fileUrl)}>
              {BTN_TXT.DELETE}
            </Button>
          )}
        </Flex>
        {uploading && <Progress percent={progress} />}
      </Flex>
    </div>
  );
};

export default BusEntDocUploadCard;
